import { useMutation } from '@tanstack/react-query';
import { renameClassroom } from 'api/classroomApi';
import { leaveClassroom } from 'api/userApi';
import { queryClient } from 'App';
import FalconCardHeader from 'components/common/FalconCardHeader';
import Flex from 'components/common/Flex';
import LoadingButton from 'components/fantaclasse/LoadingButton';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { Button, Card, Col, Form, Modal, Row } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

const LeaveClassroomModal = ({ modalShow, setModalShow }) => {
  const [isLeaving, setIsLeaving] = useState(false);
  const navigate = useNavigate();

  const leaveClassroomMutation = useMutation({
    mutationFn: leaveClassroom,
    onMutate: () => {
      setIsLeaving(true);
    },
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey: ['userData'] });
      toast.success(() => (
        <span>
          Hai abbandonato la classe <strong>{data.name}</strong>!
        </span>
      ));
      navigate('/classe');
    },
    onError: err => {
      toast.error(err.response?.data?.message || err.message);
    },
    onSettled: () => {
      setIsLeaving(false);
    }
  });

  const leaveClassroomSubmit = () => {
    leaveClassroomMutation.mutate();
  };

  return (
    <Modal
      show={modalShow}
      onHide={() => setModalShow(false)}
      size="lg"
      centered
    >
      <Modal.Header className="p-4">
        <Modal.Title id="contained-modal-title-vcenter">
          Sei sicuro di voler abbandonare la tua classe?
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <ul className="mb-0">
          <li>Perderai tutti i tuoi progressi.</li>
          <li>
            La tua squadra <strong>sarà eliminata</strong> e{' '}
            <strong>rimossa dalla classifica</strong>.
          </li>
        </ul>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => setModalShow(false)}>
          Indietro
        </Button>
        <LoadingButton
          text="Conferma"
          variant="danger"
          isLoading={isLeaving}
          action={leaveClassroomSubmit}
        />
      </Modal.Footer>
    </Modal>
  );
};

const ClassroomSettings = ({ classroom, prefect }) => {
  const [classroomName, setClassroomName] = useState(classroom.name);
  useEffect(() => {
    setClassroomName(classroom.name);
  }, [classroom]);

  const [isRenaming, setIsRenaming] = useState(false);
  const renameClassroomMutation = useMutation({
    mutationFn: renameClassroom,
    onMutate: () => {
      setIsRenaming(true);
    },
    onSuccess: data => {
      setIsRenaming(false);
      queryClient.invalidateQueries({ queryKey: ['classroomData'] });
      toast.success(() => (
        <span>
          Il nome della classe è stato modificato in{' '}
          <strong>{data.name}</strong>
        </span>
      ));
    },
    onError: err => {
      setIsRenaming(false);
      toast.error(
        err.response?.data?.message ||
          'Errore durante la modifica del nome della classe'
      );
    }
  });

  const handleSubmit = e => {
    e.preventDefault();
    renameClassroomMutation.mutate(classroomName);
  };

  const [modalShow, setModalShow] = useState(false);

  return (
    <>
      <LeaveClassroomModal modalShow={modalShow} setModalShow={setModalShow} />
      <Card className="mb-3">
        <FalconCardHeader title="Classe" />
        <Card.Body className="bg-body-tertiary">
          <Form onSubmit={handleSubmit}>
            <Row className="mb-3 g-3">
              <Form.Group as={Col} controlId="name">
                <Form.Label>Nome della classe</Form.Label>
                <Form.Control
                  placeholder="Nome della classe"
                  value={classroomName}
                  onChange={e => setClassroomName(e.target.value)}
                  disabled={!prefect}
                />
              </Form.Group>
            </Row>
            <Row className="mb-3 g-3">
              <Form.Group as={Col} controlId="teamDescription">
                <Form.Label>Codice classe</Form.Label>
                <Form.Control
                  type="text"
                  value={classroom.code || ''}
                  disabled
                />
              </Form.Group>
            </Row>
            <Flex justifyContent="between">
              <div>
                <Button variant="danger" onClick={() => setModalShow(true)}>
                  Esci dalla classe
                </Button>
              </div>
              {prefect && (
                <div>
                  <LoadingButton
                    text="Salva"
                    isLoading={isRenaming}
                    disabled={classroomName === classroom.name}
                    type="submit"
                  />
                </div>
              )}
            </Flex>
          </Form>
        </Card.Body>
      </Card>
    </>
  );
};

ClassroomSettings.propTypes = {
  classroom: PropTypes.object.isRequired,
  prefect: PropTypes.bool.isRequired
};

LeaveClassroomModal.propTypes = {
  modalShow: PropTypes.bool.isRequired,
  setModalShow: PropTypes.func.isRequired
};

export default ClassroomSettings;
